import React, { Fragment, ReactElement, useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'

import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'
import { LoadingSpinner } from '@dfds-pax/shared-components/client'

import { useTranslation } from '@/hooks/useDebugTranslations'

import TicketInfo from './TicketInfo'
import TierSelector from './TierSelector'

interface DepartureTierProps {
  components?: BffInterfacesDeparture.DeparturePricesComponent[]
  currency: string
  departureId: string
  economyProduct: string
  hidePriceAdjustment: boolean
  loading: boolean
  offerApplied: boolean
  offerOnExtras: boolean
  offerPrice?: number
  onTierSelection: () => void
  price: number
  productCode: string
  radioName: string
  selectedType: boolean
  soldOut: boolean
  ticketRef?: React.RefObject<HTMLDivElement>
  ticketTypeChanging: boolean
}

const DepartureTier = ({
  components,
  currency,
  departureId,
  economyProduct,
  hidePriceAdjustment,
  loading,
  offerPrice,
  onTierSelection,
  price,
  productCode,
  selectedType,
  soldOut,
  ticketTypeChanging,
}: DepartureTierProps): ReactElement => {
  const ticketRef = useRef<HTMLDivElement | null>(null)

  const [showSkeleton, setShowSkeleton] = useState<boolean>(true)

  const onSelection = () => {
    onTierSelection()
    if (ticketRef?.current) {
      ticketRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' })
    }
  }

  useEffect(() => {
    if (!loading) setShowSkeleton(false)
  }, [loading])

  const showLoader: boolean = (showSkeleton && productCode === economyProduct) || (ticketTypeChanging && selectedType)

  return (
    <div
      className={clsx(
        'focus-visible:outline-semantic-global-primary-default bg-semantic-global-background-white flex w-full flex-col border-solid transition-all duration-200 ease-in-out focus-visible:outline-2 focus-visible:outline-offset-2',
        selectedType ? 'border-semantic-global-primary-default border-2' : 'border-semantic-global-border-light border-2',
        (selectedType || soldOut || loading) && 'cursor-default',
        !selectedType && !soldOut && !loading && 'hover:shadow-dfds-elevated-4 cursor-pointer',
        soldOut && 'h-full',
        !soldOut && !selectedType && !loading && 'hover:border-semantic-global-primary-default'
      )}
      data-e2e="departure-tier-card"
      data-e2e-selected={selectedType}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          !selectedType && !soldOut && !loading && onSelection()
        }
      }}
      ref={ticketRef}
      tabIndex={!soldOut && !selectedType && !loading ? 0 : -1}
    >
      <div className={clsx('relative flex flex-1 flex-col')}>
        {showLoader ? (
          <div
            className={clsx('bg-semantic-global-background-white absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center')}
          >
            <div className="z-10">
              <LoadingSpinner darkModeVariant="default" size="xl" />
            </div>
          </div>
        ) : null}
        <div className={clsx(showLoader && 'opacity-50')}>
          <TierSelector
            components={components}
            currency={currency}
            departureId={departureId}
            hidePriceAdjustment={hidePriceAdjustment}
            offerPrice={offerPrice}
            onTierSelection={() => !selectedType && !soldOut && !loading && onSelection()}
            price={price}
            productCode={productCode}
            selectedType={selectedType}
            soldOut={soldOut}
          />
          <div className="flex-none" onClick={() => !selectedType && !soldOut && !loading && onSelection()}>
            <TicketInfo productCode={productCode} soldOut={soldOut} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface Props {
  departureId: string
  economyProduct: string
  flexiProduct: string
  loading: boolean
  offerApplied: boolean
  onDepartureTierSelection: (ticket: BffInterfacesDeparture.Ticket) => void
  returnLeg: boolean
  selectedType: string | false
  selectingProduct?: string
  tickets: BffInterfacesDeparture.Ticket[]
}

const DepartureTierGroup: React.FC<Props> = ({
  departureId,
  economyProduct,
  flexiProduct,
  loading,
  offerApplied,
  onDepartureTierSelection,
  returnLeg,
  selectedType,
  selectingProduct,
  tickets,
}): ReactElement => {
  const economyTier = tickets.filter((t) => t.productCode === economyProduct)[0]
  const premiumTier = tickets.filter((t) => t.productCode === flexiProduct)[0]

  const [ticketTypeChanging, setTicketTypeChanging] = useState<boolean>(false)

  // Derived, not cached: a deselected row must reset its tiers so they can be picked again.
  const confirmedProduct = selectedType ? (selectedType === premiumTier.productCode ? flexiProduct : economyProduct) : undefined
  const selectedProduct = selectingProduct ?? confirmedProduct

  useEffect(() => {
    if (!loading) setTicketTypeChanging(false)
  }, [loading])

  useEffect(() => {
    if (selectingProduct) setTicketTypeChanging(true)
  }, [selectingProduct])

  const { t } = useTranslation()

  return (
    <>
      <div className="typography-heading-sm">{t('SELECT-TICKET-TYPE')}</div>
      <div className="gap-semantic-global-md md:gap-semantic-global-lg grid grid-rows-[auto_auto] md:grid-cols-2 md:grid-rows-none">
        <DepartureTier
          components={economyTier.components}
          currency={economyTier.currency}
          departureId={departureId}
          economyProduct={economyProduct}
          hidePriceAdjustment={!offerApplied || economyTier.ticketOffer?.hidePriceAdjustment || false}
          loading={loading}
          offerApplied={offerApplied}
          offerOnExtras={economyTier.offerOnExtras}
          offerPrice={offerApplied && economyTier.ticketOffer ? economyTier.ticketOffer.price : undefined}
          onTierSelection={() => onDepartureTierSelection(economyTier)}
          price={economyTier.price}
          productCode={economyTier.productCode}
          radioName={returnLeg ? `departures-return-economy-radio` : `departures-outbound-radio`}
          selectedType={selectedProduct === economyTier.productCode}
          soldOut={economyTier?.soldOut}
          ticketTypeChanging={ticketTypeChanging}
        />
        <DepartureTier
          components={premiumTier.components}
          currency={premiumTier.currency}
          departureId={departureId}
          economyProduct={economyProduct}
          hidePriceAdjustment={!offerApplied || premiumTier.ticketOffer?.hidePriceAdjustment || false}
          loading={loading}
          offerApplied={offerApplied}
          offerOnExtras={premiumTier.offerOnExtras}
          offerPrice={offerApplied && premiumTier.ticketOffer ? premiumTier.ticketOffer.price : undefined}
          onTierSelection={() => onDepartureTierSelection(premiumTier)}
          price={premiumTier.price}
          productCode={premiumTier.productCode}
          radioName={returnLeg ? 'departures-return-premium-radio' : 'departures-outbound-premium-radio'}
          selectedType={selectedProduct === premiumTier.productCode}
          soldOut={premiumTier?.soldOut}
          ticketTypeChanging={ticketTypeChanging}
        />
      </div>
    </>
  )
}

export default DepartureTierGroup

export interface Tier {
  currency: string
  price: number
  productCode: string
  soldOut: boolean
}
